import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Truck, HardHat, Package, PenLine } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, API } from "@/lib/api";
import { eur, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import ArchivioModal from "@/components/ArchivioModal";

const todayStr = () => new Date().toISOString().slice(0, 10);
const soonStr = () => new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);
const BADGE = {
  red: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  amber: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  green: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
};

function scadBadge(date) {
  if (!date) return <span className="text-muted-foreground text-sm">—</span>;
  const t = todayStr(), s = soonStr();
  const tone = date < t ? "red" : date <= s ? "amber" : "green";
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${BADGE[tone]}`}>{fmtDate(date)}</span>;
}

function certBadge(certs) {
  certs = certs || [];
  if (!certs.length) return <span className="text-muted-foreground text-sm">—</span>;
  const t = todayStr(), s = soonStr();
  const exp = certs.filter((c) => c.scadenza && c.scadenza < t).length;
  const soon = certs.filter((c) => c.scadenza && c.scadenza >= t && c.scadenza <= s).length;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-sm text-muted-foreground">{certs.length}</span>
      {exp > 0 && <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${BADGE.red}`}>{exp} scadute</span>}
      {soon > 0 && <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${BADGE.amber}`}>{soon} in scadenza</span>}
      {exp === 0 && soon === 0 && <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${BADGE.green}`}>OK</span>}
    </div>
  );
}

const TABS = [
  { key: "fornitori", label: "Fornitori", icon: Package },
  { key: "operai", label: "Operai", icon: HardHat },
  { key: "mezzi", label: "Mezzi", icon: Truck },
  { key: "direttori", label: "Direttori di cantiere", icon: PenLine, ownerOnly: true },
];

function firmaThumb(it) {
  if (!it.firma_file_id) return <span className="text-muted-foreground text-sm">— nessuna firma</span>;
  return <img src={`${API}/uploads/${it.firma_file_id}`} alt="firma" className="h-9 max-w-[140px] object-contain" data-testid={`direttore-firma-${it.id}`} />;
}

const COLUMNS = {
  fornitori: [
    { key: "ragione_sociale", label: "Ragione Sociale" },
    { key: "categoria", label: "Categoria" },
    { key: "piva", label: "P.IVA" },
    { key: "telefono", label: "Telefono" },
    { key: "email", label: "Email" },
  ],
  operai: [
    { key: "nome", label: "Nome" },
    { key: "mansione", label: "Mansione" },
    { key: "telefono", label: "Telefono" },
    { key: "paga_oraria", label: "Paga Oraria", money: true },
    { key: "certificazioni", label: "Certificazioni", render: (it) => certBadge(it.certificazioni) },
  ],
  mezzi: [
    { key: "nome", label: "Nome / Targa" },
    { key: "tipo", label: "Tipo" },
    { key: "costo_orario", label: "Costo Orario", money: true },
    { key: "scadenza_revisione", label: "Revisione", render: (it) => scadBadge(it.scadenza_revisione) },
    { key: "scadenza_assicurazione", label: "Assicurazione", render: (it) => scadBadge(it.scadenza_assicurazione) },
  ],
  direttori: [
    { key: "nome", label: "Nome e Cognome" },
    { key: "firma_file_id", label: "Firma", render: (it) => firmaThumb(it) },
  ],
};

function ArchivioTable({ tipo, isOwner }) {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const cols = COLUMNS[tipo];

  const load = useCallback(async () => setItems(await api.get(`/${tipo}`)), [tipo]);
  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    try {
      await api.del(`/${tipo}/${toDelete.id}`);
      toast.success("Spostato nel cestino.");
      setToDelete(null); load();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => { setEditing(null); setModal(true); }} data-testid={`add-${tipo}-button`}
          className="gap-2 bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-slate-900">
          <Plus className="h-4 w-4" /> Aggiungi
        </Button>
      </div>
      <Card>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <div className="py-14 text-center text-sm text-muted-foreground">Nessun elemento in archivio.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {cols.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}
                    <TableHead className="w-20"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.id} data-testid={`${tipo}-row-${it.id}`}>
                      {cols.map((c) => (
                        <TableCell key={c.key} className={c.key === cols[0].key ? "font-medium" : "text-muted-foreground text-sm"}>
                          {c.render ? c.render(it) : c.money ? eur(it[c.key]) : (it[c.key] || "—")}
                        </TableCell>
                      ))}
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-${tipo}-${it.id}`} onClick={() => { setEditing(it); setModal(true); }}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          {isOwner && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" data-testid={`delete-${tipo}-${it.id}`} onClick={() => setToDelete(it)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ArchivioModal open={modal} onOpenChange={setModal} tipo={tipo} initial={editing} onSaved={load} />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Spostare nel cestino?</AlertDialogTitle>
            <AlertDialogDescription>Potrai ripristinarlo dalla sezione Cestino.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function Archivio() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [params] = useSearchParams();
  const initialTab = params.get("tab") || "fornitori";
  const tabs = TABS.filter((t) => !t.ownerOnly || isOwner);
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Archivio</h1>
        <p className="text-sm text-muted-foreground mt-1">Anagrafiche di fornitori, operai, mezzi e direttori di cantiere.</p>
      </div>
      <Tabs defaultValue={initialTab}>
        <TabsList data-testid="archivio-tabs">
          {tabs.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`} className="gap-2">
              <t.icon className="h-4 w-4" /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {tabs.map((t) => (
          <TabsContent key={t.key} value={t.key} className="mt-4">
            <ArchivioTable tipo={t.key} isOwner={isOwner} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
